import './styles.scss';

import { useMutation } from '@apollo/client';
import Button from '@ets-global/b2c-website-ui/Button';
import { fromJS } from 'immutable';
import kebabCase from 'lodash/kebabCase';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useReducer } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';

import BaseModal from '../../../components/Base/Modal/BaseModal';
import CheckoutSummaryLayout from '../../../components/Layout/BuyingProcess/Session/CheckoutSummaryLayout';
import LocalizedRedirect from '../../../components/Routing/LocalizedRedirect';
import SessionRegistrationHeader from '../../../components/SessionRegistration/Header';
import { CREATE_SESSION_PURCHASE } from '../../../graphql/mutations/Purchase/createSessionPurchase';
import { REGION_CITY_BY_COORDINATES } from '../../../graphql/queries';
import errorsHandler from '../../../helpers/errorsHandler';
import { canSeePrepTools, getPrevSearch } from '../../../helpers/sessionRegister';
import { useBasketCookie, useDataLayer, useNotification, useQuery, useRouter } from '../../../hooks';
import { paths } from '../../../paths';
import { AuthContext } from '../../../store/Auth';
import ModalContext from '../../../store/Modal/ModalContext';
import CandidateWidgetSkeleton from './Candidate/Widget/CandidateWidgetSkeleton';
import Candidates from './Candidates';
import SessionRegisterMeta from './SessionRegisterMeta';

const reducer = (state, action) => {
  switch (action.type) {
    case 'startPurchaseCandidates':
      return { addCandidatesInSessionLoading: true, areCandidatesValid: false };
    case 'finishPurchase':
      return { addCandidatesInSessionLoading: false, areCandidatesValid: true };
    default:
      throw new Error('Action type is invalid !');
  }
};

const SessionRegister = ({
  openSession = {},
  productsAndServices,
  purchase,
  purchaseRefetch,
  sessionLoading,
  purchaseLoading,
}) => {
  const history = useHistory();
  const location = useLocation();
  const {
    lang,
    generatePath,
    currentRoute: { params },
  } = useRouter();
  const { pushEventLoadedPurchaseFunnel, pushEventFormError } = useDataLayer();
  const { getPromoCodeOrVoucher } = useBasketCookie();
  const promoCodeOrVoucher = getPromoCodeOrVoucher();
  const { newNotification } = useNotification();
  const { t } = useTranslation();
  const { showModal, hideModal } = useContext(ModalContext);
  const { user } = useContext(AuthContext);
  const [createSessionPurchase] = useMutation(CREATE_SESSION_PURCHASE);
  const [state, dispatch] = useReducer(reducer, {
    addCandidatesInSessionLoading: false,
    areCandidatesValid: true,
  });

  const sessionId = parseInt(params.sessionId, 10);
  const session = fromJS(openSession);

  useEffect(() => {
    pushEventLoadedPurchaseFunnel({
      type: 'test order',
      stepName: 'candidate information',
      stepNumber: 2,
    });
  }, []);

  //Getting test site coordinate and region
  let sessionCoordinate = null;
  if (session.get('testingSite')) {
    sessionCoordinate = session.get('testingSite').get('address').get('coordinates').toJS();
  }
  const {
    data: { regionCity },
  } = useQuery(REGION_CITY_BY_COORDINATES, {
    variables: {
      lang,
      coordinates: sessionCoordinate,
    },
    skip: !sessionCoordinate,
  });

  const hasPurchase = !!purchase;
  const changeSession = hasPurchase && purchase.openSession && purchase.openSession.id !== sessionId;
  let candidates = [];
  if (promoCodeOrVoucher?.candidate) {
    candidates = [promoCodeOrVoucher?.candidate];
  }
  let canAddCandidates = true;

  //Set candidates from purchase
  if (hasPurchase) {
    candidates = purchase.candidates.map(({ __typename, productsAndServices, ...candidate }) => ({
      ...candidate,
      productsAndServices: productsAndServices.map(({ __typename, ...productAndService }) => ({
        ...productAndService,
      })),
    }));

    canAddCandidates = !purchase.voucher || (purchase.voucher && !purchase.voucher.isCpf);
  }
  const candidatesCount = candidates.length;

  //Update P&S when purchase is updated with another session
  const updatePurchaseSessionAndCandidates = (availableProductsAndServices) => {
    if (hasPurchase) {
      let allPSIds = [];
      availableProductsAndServices.correctionProductsAndServices.forEach((ps) => {
        allPSIds.push(ps.id);
      });
      availableProductsAndServices.discountProductsAndServices.forEach((ps) => {
        allPSIds.push(ps.id);
      });
      availableProductsAndServices.certificateProductsAndServices.forEach((ps) => {
        allPSIds.push(ps.id);
      });

      const updatedCandidates = candidates.map(
        ({ productsAndServices, identificationId, token, pictureUrl, ...candidate }) => ({
          ...candidate,
          productsAndServices: productsAndServices.filter((ps) => allPSIds.includes(ps.id)),
        })
      );

      setPurchaseCandidates({
        registrationId: purchase.registrationId,
        candidates: updatedCandidates,
      });
    }
  };

  useEffect(() => {
    if (session.size) {
      if (!sessionLoading && changeSession) {
        if (!candidates[0].dateOfBirth && session.get('testType').get('isDateOfBirthRequired')) {
          setPurchaseCandidates({
            registrationId: purchase.registrationId,
            candidates: [],
          });
        } else {
          updatePurchaseSessionAndCandidates(productsAndServices);
        }
      }
    }
  }, [sessionId, sessionLoading]);

  //Update candidates to purchase
  const setPurchaseCandidates = ({ registrationId = null, candidates }, callback = () => {}) => {
    dispatch({ type: 'startPurchaseCandidates' });

    let variables = {
      sessionId,
      registrationId,
      purchaserId: user?.id ? parseInt(user.id, 10) : null,
      candidates: candidates.map(({ identificationId, token, pictureUrl, ...candidate }) => {
        if (candidate.dateOfBirth) {
          const date = new Date(candidate.dateOfBirth);
          candidate.dateOfBirth = `${`0${date.getDate()}`.slice(-2)}/${`0${date.getMonth() + 1}`.slice(
            -2
          )}/${date.getFullYear()}`;
        }

        return {
          ...candidate,
        };
      }),
      isCpfRequired: promoCodeOrVoucher?.isCpfRequired ?? false,
    };

    if (!purchase && promoCodeOrVoucher) {
      variables = {
        ...variables,
        discount: promoCodeOrVoucher.discountCode,
        preptools: promoCodeOrVoucher?.prepToolIds?.map((id) => ({
          id,
          quantity: 1,
        })),
        referrer: promoCodeOrVoucher?.referrer,
      };
    }

    return createSessionPurchase({ variables })
      .then(({ data: { createSessionPurchase } }) => {
        if (createSessionPurchase.__typename === 'CandidatesAddedInSessionSuccessfully') {
          if (changeSession) {
            newNotification({
              content:
                candidates.length > 0
                  ? t('session-registration.warning.options-changed.content')
                  : t('session-registration.warning.remove-candidates.content'),
              type: 'warning',
            });
          }

          if (!hasPurchase) {
            const registrationId = createSessionPurchase.registrationId;
            history.replace(
              generatePath(paths.SESSION_REGISTER_BY_REGISTRATION_ID, {
                sessionId,
                registrationId,
              })
            );
          } else {
            purchaseRefetch().then(() => {
              dispatch({ type: 'finishPurchase' });
              callback(true);
            });
          }
        }

        if (createSessionPurchase.__typename === 'DuplicatedCandidates') {
          let errorMessage = t('session-registration.candidates.duplicate.content-switch-session');
          // couldn't switch session in existing purchase because the candidate is already register for this session
          let options = {
            isOpen: true,
            inlineMode: true,
            title: <Trans>session-registration.candidates.duplicate.title</Trans>,
            children: <div>{errorMessage}</div>,
            footer: (
              <Button onClick={hideModal}>
                <Trans>session-registration.candidates.back</Trans>
              </Button>
            ),
          };

          if (!changeSession) {
            errorMessage = t('session-registration.candidates.duplicate.content');
            // couldn't add this candidate because he is already registered for this session
            dispatch({ type: 'finishPurchase' });

            options = {
              isOpen: true,
              onClose: hideModal,
              title: <Trans>session-registration.candidates.duplicate.title</Trans>,
              children: <div>{errorMessage}</div>,
              footer: (
                <Button onClick={hideModal} data-cypress={'buying-process.duplicate-modal-button'}>
                  <Trans>session-registration.candidates.duplicate.accept</Trans>
                </Button>
              ),
            };
          }

          pushEventFormError(errorMessage);
          showModal(BaseModal, options);
          callback(false);
        }

        if (createSessionPurchase.__typename === 'CandidatesCanNotBeAddedInSession') {
          let options = {
            isOpen: true,
            inlineMode: true,
            onClose: hideModal,
            title: <Trans>session-registration.candidates.duplicate.title</Trans>,
            children: (
              <div>
                {createSessionPurchase.errorCodes &&
                  createSessionPurchase.errorCodes.map((errorCode) => (
                    <div key={errorCode}>
                      <Trans>{`session-registration.${errorCode.toLowerCase()}`}</Trans>
                    </div>
                  ))}
                {(!createSessionPurchase.errorCodes || createSessionPurchase.errorCodes.length === 0) &&
                  createSessionPurchase.errorList &&
                  createSessionPurchase.errorList.map((error) => (
                    <div key={error}>
                      <Trans>{`session-registration.${
                        error.reason.toLowerCase() || error.message.toLowerCase()
                      }`}</Trans>
                    </div>
                  ))}
                {!createSessionPurchase.errorCodes && !createSessionPurchase.errorList && (
                  <div key={`unknow-error`}>
                    <Trans>{`error-page.500.content`}</Trans>
                  </div>
                )}
              </div>
            ),
            footer: (
              <Button to={paths.SESSION_SEARCH} search={getPrevSearch(session, location)} onClick={hideModal}>
                <Trans>session-registration.candidates.back</Trans>
              </Button>
            ),
          };

          showModal(BaseModal, options);
          callback(false);
        }

        dispatch({ type: 'finishPurchase' });
      })
      .catch((error) => {
        errorsHandler({ error, newNotification, t });
        dispatch({ type: 'finishPurchase' });
        callback(false);
      });
  };

  //Redirect with testing site country
  if (!sessionLoading && session.get('testingSite')) {
    const country = session.get('testingSite').get('address').get('country');
    if (country && country.get('iso2') && country.get('iso2').toLowerCase() !== params.country) {
      return (
        <LocalizedRedirect
          status={301}
          to={generatePath(paths.SESSION_REGISTER, {
            ...params,
            country: country.get('iso2').toLowerCase(),
          })}
        />
      );
    }
  }

  const loading = purchaseLoading || sessionLoading || changeSession;

  let prevLinkParams = null;
  if (regionCity) {
    prevLinkParams = {
      city: kebabCase(regionCity.city),
      region: kebabCase(regionCity.region),
    };
  }

  return (
    <>
      {!sessionLoading && session.size && <SessionRegisterMeta session={session} />}
      <CheckoutSummaryLayout
        session={session}
        productsAndServices={productsAndServices}
        prevLinkProps={{
          to: generatePath(paths.SESSION_SEARCH, prevLinkParams),
          search: getPrevSearch(session, location),
        }}
        className={'candidate'}
        purchase={purchase}
        loading={loading}
      >
        <div className="checkout-summary-layout__heading">
          <SessionRegistrationHeader
            title={'session-registration.candidate.info'}
            subtitle={'session-registration.candidate.about-you'}
            count={candidatesCount}
          />
          {!loading && (
            <Button
              disabled={
                candidatesCount === 0 ||
                !state.areCandidatesValid ||
                state.addCandidatesInSessionLoading ||
                sessionLoading ||
                purchaseLoading
              }
              to={
                purchase
                  ? canSeePrepTools(purchase, session)
                    ? generatePath(paths.SESSION_CROSS_SELLING, {
                        sessionId,
                        registrationId: purchase.registrationId,
                      })
                    : generatePath(paths.PURCHASE_SUMMARY, {
                        registrationId: purchase.registrationId,
                      })
                  : null
              }
              data-cypress="buying-process.next-step"
            >
              <Trans>session-registration.next.step</Trans>
            </Button>
          )}
        </div>

        {loading ? (
          <CandidateWidgetSkeleton />
        ) : (
          <Candidates
            candidates={candidates}
            session={session}
            canAddCandidates={canAddCandidates}
            productsAndServices={productsAndServices}
            sessionId={sessionId}
            registrationId={purchase?.registrationId}
            promoCodeOrVoucher={promoCodeOrVoucher}
            loading={state.addCandidatesInSessionLoading}
            setPurchaseCandidates={setPurchaseCandidates}
          />
        )}
      </CheckoutSummaryLayout>
    </>
  );
};

SessionRegister.propTypes = {
  openSession: PropTypes.object,
  purchase: PropTypes.object,
  purchaseRefetch: PropTypes.func,
  openSessionLoading: PropTypes.bool,
  purchaseLoading: PropTypes.bool,
};

const areEquals = (
  { openSession: prevOpenSession, purchase: prevPurchase = {} },
  { openSession: nextOpenSession, purchase: nextPurchase = {} }
) => {
  return (
    prevPurchase &&
    prevOpenSession &&
    fromJS(prevOpenSession).equals(fromJS(nextOpenSession)) &&
    fromJS(prevPurchase).equals(fromJS(nextPurchase))
  );
};

export default React.memo(SessionRegister, areEquals);
